import {
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { Address } from "@blaze-cardano/core";

import { GummiwormContext } from "./context";
import type { IGummiwormState } from "./types";
import { useDepositTxs } from "./hooks/useDepositTxs";
import type { IDepositHistoryTx } from "./hooks/useDepositTxs";
import { useWithdrawTxs } from "./hooks/useWithdrawTxs";
import { useAppContext } from "../AppContext";
import { Core } from "@blaze-cardano/sdk";
import { useWebSocket, type WsAuthSigner } from "@/hooks/useWebSocket";
import { ensureGuestWsIdentity } from "@/lib/guestIdentity";
import { signWithEphemeralWallet } from "@/lib/ephemeralWallet";
import { useReDelegateKey } from "@/hooks/useReDelegateKey";
import {
  needsRequestPolling,
  polledStatusUpdate,
  rejectionUpdate,
  shouldRequestSignature,
  shouldSubmitDeposit,
} from "@/lib/depositLifecycle";

export const MOCK_USER_ACCOUNT_ID = 1n;

export const GummiwormProvider: FC<PropsWithChildren> = ({ children }) => {
  const {
    state: { blaze, provider },
  } = useAppContext();
  const { mainAddress } = useWalletObserver();
  const { depositTxs, addDepositTx, updateDepositTx, clearDepositTxs } = useDepositTxs();
  const { withdrawTxs, addWithdrawTx, updateWithdrawTx, clearWithdrawTxs } = useWithdrawTxs();
  const paymentPartHash = useMemo(() => {
    if (!mainAddress) return null;
    return Address.fromBech32(mainAddress).getProps().paymentPart?.hash ?? null;
  }, [mainAddress]);
  // Market data (order book, candles, tradingEnabled) is only ever pushed over this
  // per-connection channel, so it must open even without a wallet. A guest identity
  // never matches a real deposit account, so myAccountId/ledgerView.account (below)
  // stay null and all wallet-owner gating elsewhere is unaffected.
  const wsAccountHash = useMemo(
    () => paymentPartHash ?? ensureGuestWsIdentity(),
    [paymentPartHash],
  );
  const wsUrl = `${window.__APP_CONFIG.apiWsUrl}/ws`;

  // Sign the WS private-subscribe auth with the local ephemeral (delegated)
  // key, so account/orders/deposits need no wallet prompt. Undefined for a
  // guest -- guests own no private data and skip those channels.
  const signAuth = useMemo<WsAuthSigner | undefined>(() => {
    if (!mainAddress || !provider) return undefined;
    return async (message: string) => {
      const { key, signature } = await signWithEphemeralWallet(
        provider,
        mainAddress,
        Buffer.from(message, "utf8"),
      );
      return { coseKey: key, coseSignature: signature };
    };
  }, [mainAddress, provider]);

  // Recovery: when the server rejects our delegated-key auth (the key is not
  // registered, or localStorage was cleared), re-establish it with the main
  // wallet. Reads the latest account nonce via a ref so a fresh account (nonce
  // 0) registers with nonce 1. Throttled so a run of rejections is at most one
  // wallet prompt per minute.
  const reDelegate = useReDelegateKey();
  const accountNonceRef = useRef(0);
  const reDelegating = useRef(false);
  const lastReDelegateAt = useRef(0);
  const onAuthError = useCallback(
    (code: string) => {
      if (!mainAddress || reDelegating.current) return;
      // Only a deposited account exists on the ledger to delegate for. Without
      // this, a wallet that connected but never deposited would get a spurious
      // signing prompt on every auth rejection.
      if (depositTxs.size === 0) return;
      const now = Date.now();
      if (now - lastReDelegateAt.current < 60_000) return;
      reDelegating.current = true;
      lastReDelegateAt.current = now;
      console.warn(`[ws] private auth rejected (${code}); re-establishing delegated key`);
      reDelegate(accountNonceRef.current)
        .catch((e: unknown) => console.error("[ws] re-delegation failed", e))
        .finally(() => {
          reDelegating.current = false;
        });
    },
    [mainAddress, reDelegate, depositTxs],
  );

  // Reads use the plaintext channel protocol (no encryption); writes still use
  // `orderEncryptionKey` on the HTTP submit path, so it stays in context above.
  const { ledgerView, connected: wsConnected, error: wsError, send: sendWsMessage, watchMarket } = useWebSocket(wsUrl, wsAccountHash, signAuth, onAuthError);
  // Keep the recovery nonce base current without re-subscribing the socket.
  accountNonceRef.current = ledgerView.account ? Number(ledgerView.account.nonce) : 0;
  const markets = useMemo(() => Object.values(ledgerView.markets) as any[], [ledgerView.markets]);
  const accountsReady = wsConnected;

  const tradingEnabled = useMemo(() => {
    if (!ledgerView) return false;
    return ledgerView.tradingEnabled;
  }, [ledgerView]);

  const myAccountId = useMemo(() => {
    if (!paymentPartHash) return null;
    // The account id is the payment key hash as a hex string, matching the wire
    // (Ed25519KeyHash serializes as hex) — no u256 conversion.
    return paymentPartHash;
  }, [paymentPartHash]);

  useEffect(() => {
    for (let deposit of Object.values(ledgerView.deposits)) {
      if (deposit.absorbedInBlock !== 0n) {
        if (depositTxs.has(deposit.requestId.toString())) {
          updateDepositTx(deposit.requestId, { status: "absorbed" });
        }
        continue;
      }
      if (deposit.rejectedInBlock !== 0n) {
        // Hash-aware: a deposit rejected before its L1 tx was ever submitted
        // has no UTxO to refund, so it must not enter the refund flow.
        const existing = depositTxs.get(deposit.requestId.toString());
        updateDepositTx(
          deposit.requestId,
          rejectionUpdate(existing ?? { hash: "" }, deposit.failureReason),
        );
        continue;
      }
      if (deposit.refundTx) {
        // refundTx is only ever set by the BlockConfirmed event, so its
        // presence in the ledger view IS the hard-confirmation signal, and
        // this copy of the refund is the multisigned one.
        const existing = depositTxs.get(deposit.requestId.toString());
        if (existing) {
          const refundCbor = Buffer.from(deposit.refundTx).toString("hex");
          let refundTxHash = existing.refundTxHash;
          if (!refundTxHash) {
            try {
              refundTxHash = Core.Transaction.fromCbor(
                Core.TxCBOR(refundCbor),
              ).getId();
            } catch (err) {
              console.error("Failed to compute refund tx hash:", err);
            }
          }
          // Never downgrade a status that has already progressed past
          // confirmation (settling, on-chain, refund flow, ...): only
          // promote new/pending, otherwise leave status untouched.
          const update: Partial<IDepositHistoryTx> = {
            refundCbor,
            refundTxHash,
            hardConfirmed: true,
          };
          if (["new", "pending"].includes(existing.status)) {
            update.status = "confirmed";
          }
          updateDepositTx(deposit.requestId, update);
        }
        continue;
      }
      if (!depositTxs.has(deposit.requestId.toString())) {
        addDepositTx(deposit.requestId, {
          cbor: "",
          hash: "",
          refundCbor: "",
          status: "pending",
        });
      } else {
        updateDepositTx(deposit.requestId, { status: "pending" });
      }
    }
  }, [ledgerView.deposits]);

  // ── Refund tx poller ────────────────────────────────────────────────
  // Hydrozoa mints a post-dated refund tx for every registered deposit as
  // a safety valve, and no longer pushes it to us via the ledger event log
  // (sugar-rush stopped tailing hydrozoa's rocksdb directly). Poll hydrozoa's
  // HTTP API for each deposit that doesn't have one yet.
  const pollingRefunds = useRef(new Set<string>());
  const depositTxsRef = useRef(depositTxs);
  useEffect(() => {
    depositTxsRef.current = depositTxs;
  }, [depositTxs]);

  useEffect(() => {
    const pollOnce = async () => {
      const pending = [...depositTxsRef.current.entries()].filter(([, tx]) =>
        needsRequestPolling(tx),
      );
      if (pending.length === 0) return;

      await Promise.all(
        pending.map(async ([id]) => {
          if (pollingRefunds.current.has(id)) return;
          pollingRefunds.current.add(id);
          try {
            const response = await fetch(
              `${window.__APP_CONFIG.apiBaseUrl}/head/requests/${id}`,
            );
            if (!response.ok) return;
            const data = await response.json();
            const update: Partial<IDepositHistoryTx> = {};

            // Hard confirmation: hydrozoa's request status is the
            // authoritative source (the ws BlockConfirmed path is dead until
            // the wire contract carries ProxyBlockConfirmation — see
            // depositLifecycle.ts).
            const current = depositTxsRef.current.get(id);
            if (current) {
              Object.assign(
                update,
                polledStatusUpdate(current.status, data) ?? {},
              );
            }

            const refund = data.status?.relatedEffects?.find(
              (e: any) => e.kind == "refund",
            );
            if (refund && !current?.refundCbor) {
              const refundTxId = refund.l1TxId;
              const getRefundBody = await fetch(
                `${window.__APP_CONFIG.apiBaseUrl}/head/effects/${refundTxId}`,
              );
              if (getRefundBody.ok) {
                const effectData = await getRefundBody.json();
                if (effectData.txCbor) {
                  update.refundCbor = effectData.txCbor;
                  update.refundTxHash = refundTxId;
                }
              }
            }

            if (Object.keys(update).length > 0) {
              updateDepositTx(BigInt(id), update);
            }
          } catch (err) {
            console.error("Error polling hydrozoa deposit status:", err);
          } finally {
            pollingRefunds.current.delete(id);
          }
        }),
      );
    };

    pollOnce();
    const interval = window.setInterval(pollOnce, 10_000);
    return () => window.clearInterval(interval);
  }, [updateDepositTx]);

  // ── Deposit reconciler ──────────────────────────────────────────────
  // Single place that handles L1 submission and confirmation monitoring.
  // Runs whenever depositTxs or blaze changes. Uses a ref to track which
  // deposits are already being handled so we don't double-submit.
  const handling = useRef(new Set<string>());

  useEffect(() => {
    if (!blaze || !blaze.provider) return;

    for (const [id, tx] of depositTxs.entries()) {
      if (handling.current.has(id)) continue;

      // Fallback only: deposits normally sign at click time in
      // useHandleDepositRequest. Entries persisted by builds that deferred
      // the signature can still hold an unsigned tx — prompt for those.
      if (shouldRequestSignature(tx)) {
        handling.current.add(id);
        (async () => {
          try {
            const unsigned = Core.Transaction.fromCbor(
              Core.TxCBOR(tx.unsignedCbor!),
            );
            const signed = await blaze.signTransaction(unsigned);
            updateDepositTx(BigInt(id), { cbor: signed.toCbor() });
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            updateDepositTx(BigInt(id), {
              status: "failed",
              errorMessage: `Deposit was not signed: ${msg}`,
            });
          } finally {
            handling.current.delete(id);
          }
        })();
        continue;
      }

      // Ready to submit: signed, refund tx in hand, AND the deposit's L2
      // block is hard confirmed (see shouldSubmitDeposit) — never earlier.
      if (shouldSubmitDeposit(tx)) {
        handling.current.add(id);
        (async () => {
          try {
            const signedTx = Core.Transaction.fromCbor(Core.TxCBOR(tx.cbor));
            const hash = await blaze.submitTransaction(signedTx);
            updateDepositTx(BigInt(id), { status: "settling", hash });
          } catch (err) {
            const msg = err instanceof Error ? err.message : String(err);
            if (msg.includes("already submitted") || msg.includes("AlreadyExists")) {
              // Someone else submitted — that's fine
            } else {
              updateDepositTx(BigInt(id), {
                status: "failed",
                errorMessage: `L1 transaction submission failed: ${msg}`,
              });
            }
          } finally {
            handling.current.delete(id);
          }
        })();
      }

      // Settling: monitor for L1 confirmation
      if (tx.status === "settling" && tx.hash) {
        handling.current.add(id);
        blaze.provider
          .awaitTransactionConfirmation(tx.hash, 120_000)
          .then((confirmed) => {
            if (confirmed) {
              updateDepositTx(BigInt(id), { status: "on-chain" });
            } else {
              updateDepositTx(BigInt(id), {
                status: "failed",
                errorMessage:
                  "Deposit transaction was not confirmed on L1 within the expected window.",
              });
            }
          })
          .catch((err) => {
            console.error("Error monitoring L1 tx confirmation:", err);
          })
          .finally(() => {
            handling.current.delete(id);
          });
      }

      // Rejected with refund available: submit refund tx
      if (tx.status === "rejected" && tx.refundCbor && !tx.hash) {
        handling.current.add(id);
        (async () => {
          try {
            updateDepositTx(BigInt(id), { status: "refunding" });
            const refundTx = Core.Transaction.fromCbor(Core.TxCBOR(tx.refundCbor));
            const refundHash = await blaze.submitTransaction(refundTx);
            updateDepositTx(BigInt(id), { hash: refundHash, status: "refunding" });
            const confirmed = await blaze.provider.awaitTransactionConfirmation(
              refundHash,
              120_000,
            );
            if (confirmed) {
              updateDepositTx(BigInt(id), { status: "refunded" });
            }
          } catch (err) {
            console.error("Error submitting refund tx:", err);
          } finally {
            handling.current.delete(id);
          }
        })();
      }

      // Refunding: monitor for confirmation
      if (tx.status === "refunding" && tx.hash) {
        handling.current.add(id);
        blaze.provider
          .awaitTransactionConfirmation(tx.hash, 120_000)
          .then((confirmed) => {
            if (confirmed) {
              updateDepositTx(BigInt(id), { status: "refunded" });
            }
          })
          .catch((err) => {
            console.error("Error monitoring refund tx:", err);
          })
          .finally(() => {
            handling.current.delete(id);
          });
      }
    }
  }, [depositTxs, blaze, updateDepositTx]);

  const contextValue = useMemo<IGummiwormState>(
    () => ({
      accountsReady,
      markets,
      myAccountId,
      error: wsError,
      tradingEnabled,
      depositTxs,
      clearDepositTxs,
      withdrawTxs,
      addWithdrawTx,
      updateWithdrawTx,
      clearWithdrawTxs,
      sendWsMessage,
      watchMarket,
      ledgerView,
    }),
    [wsConnected, wsError, markets, myAccountId, tradingEnabled, depositTxs, clearDepositTxs, withdrawTxs, addWithdrawTx, updateWithdrawTx, clearWithdrawTxs, sendWsMessage, watchMarket, ledgerView],
  );

  return (
    <GummiwormContext.Provider value={contextValue}>
      {children}
    </GummiwormContext.Provider>
  );
};
